/// <reference types="@angular/localize" />

import { APP_INITIALIZER, enableProdMode, ErrorHandler, importProvidersFrom } from '@angular/core';

import { HTTP_INTERCEPTORS, provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import { bootstrapApplication, BrowserModule } from '@angular/platform-browser';
import { provideAnimations } from '@angular/platform-browser/animations';
import { provideRouter } from '@angular/router';
import { LicenseServiceGrid, RoleTemplateServiceGrid } from '@myapp/features/admin/services';
import { AuthorizationService, ConfirmationDialogHtmlPaneService, ConfirmationDialogService, UiWindowService, UserService } from '@myapp/modules/fwcore/services';
import { NgbAlertModule, NgbModule } from '@ng-bootstrap/ng-bootstrap';
import { NgIdleKeepaliveModule } from '@ng-idle/keepalive';
import { OktaAuthGuard, OktaAuthModule, OktaCallbackComponent, OKTA_CONFIG } from '@okta/okta-angular';
import { OktaAuth } from '@okta/okta-auth-js';
import { NotificationService } from '@progress/kendo-angular-notification';
import { AppComponent } from './app/app.component';
import { FwcoreModule } from './app/modules/fwcore/fwcore.module';
import { ApplicationInsightsErrorHandler } from './app/modules/fwcore/handlers';
import { HomePageComponent, LoginComponent, PageNoLicenseComponent, PageNotAuthorizedComponent, PageNotFoundComponent } from './app/modules/fwcore/pages';
import { environment } from './environments/environment';
import { ErrorIntercept } from './interceptors/error.interceptor';
import { InterceptorDateHttp } from './interceptors/interceptorDateHttp';
import { LoaderInterceptor } from './interceptors/interceptorLoader';
import { ChartsModule } from '@progress/kendo-angular-charts';
import { initSettingsTabs } from './app/modules/fwcore/components/user-settings/user-settings.func';
import { provideLottieOptions } from 'ngx-lottie';
import { ContractsearchComponent } from './app/features/ei/ckb/ckb/pages/contractsearch/contractsearch.component';
import { LoginTrackerService } from '@mysrc/services/framework';

export function getBaseUrl() {
  return document.getElementsByTagName('base')[0].href;
}

export function initLoginTracker(loginTracker: LoginTrackerService) {
  return () => {
    // LoginTrackerService will automatically initialize when UserService is ready
    console.log('Login tracker initialized');
  };
}

// Get okta settings before bootstrapping
fetch('/assets/configuration/appconfig.json')
  .then(response => response.json())
  .then(config => {

    const providersConfig = [
      { provide: 'BASE_URL', useFactory: getBaseUrl, deps: [] },
      { provide: 'CONFIG_SETTINGS', useValue: config }
    ];

    if (environment.production) {
      enableProdMode();
    }

    bootstrapApplication(AppComponent
      , {
        providers: [
          importProvidersFrom(
            BrowserModule
            , OktaAuthModule
            , NgbModule
            , NgbAlertModule
            , FwcoreModule
            , ChartsModule
            , NgIdleKeepaliveModule.forRoot()
          )
          , provideAnimations()
          , providersConfig
          , {
            provide: OKTA_CONFIG, useFactory: (config: 'CONFIG_SETTINGS') => {
              const oktaSettings = config[<any>'okta'];
              const oktaConfig = {
                issuer: oktaSettings[<any>'issuer']
                , redirectUri: oktaSettings[<any>'redirectUri']
                , postLogoutRedirectUri: oktaSettings[<any>'postLogoutRedirectUri']
                , clientId: oktaSettings[<any>'clientId']
                , scopes: Array.from(oktaSettings[<any>'scopes'])
                , pkce: JSON.parse(oktaSettings[<any>'pkce'])
              };
              const oktaAuth = new OktaAuth(oktaConfig);
              return { oktaAuth };
            }
            , deps: ['CONFIG_SETTINGS']
          }
          , {
            provide: HTTP_INTERCEPTORS
            , useClass: InterceptorDateHttp
            , multi: true
          }
          , {
            provide: HTTP_INTERCEPTORS
            , useClass: LoaderInterceptor
            , multi: true
          }
          , {
            provide: HTTP_INTERCEPTORS
            , useClass: ErrorIntercept
            , multi: true
          }
          , {
            provide: APP_INITIALIZER
            , useFactory: initSettingsTabs
            , deps: [UserService]
            , multi: true
          }
          , {
            provide: APP_INITIALIZER
            , useFactory: initLoginTracker
            , deps: [LoginTrackerService]
            , multi: true
          }
          , {
            provide: ErrorHandler
            , useClass: ApplicationInsightsErrorHandler
          }
          , provideHttpClient(withInterceptorsFromDi())
          , provideAnimations()
          , UiWindowService
          , NotificationService
          , ConfirmationDialogService
          , ConfirmationDialogHtmlPaneService
          , LicenseServiceGrid
          , RoleTemplateServiceGrid
          , provideLottieOptions({
            player: () => import(/* webpackChunkName: 'lottie-web' */ 'lottie-web'),
          })
          , provideRouter([
            { path: '', redirectTo: 'login', pathMatch: 'full' }
            , { path: 'home', component: HomePageComponent, canActivate: [OktaAuthGuard] }
            , { path: 'login', component: LoginComponent }
            , { path: 'login/callback', component: OktaCallbackComponent }
            , {
              path: 'admin'
              , loadChildren: () => import('./app/features/admin/admin.routes')
                .then((r => r.ADMIN_ROUTES))
            }
            , {
              path: 'ei'
              , loadChildren: () => import('./app/features/ei/ei.routes')
                .then((r => r.EI_ROUTES))
            }

            , {
              path: 'ckb'
              , loadChildren: () => import('./app/features/ei/ckb/ckb/ckb.route')
                .then((r => r.CKB_ROUTES))            
              , data: { shareinternally: false, applicationid: "22AC4A03-7EE5-4FC4-AA0B-976DE5FE3911", hierarchyId:"555BF46E-28C4-4137-B1DF-54A606D6D8F4"  }  
            }

            , {
              path: 'rkb'
              , loadChildren: () => import('./app/features/ei/ckb/ckb/ckb.route')
                .then((r => r.CKB_ROUTES))
              , data: { shareinternally: true, applicationid: "2E940D05-0C7B-498A-8066-F7DAAA403CC7", hierarchyId: "F26AD00F-F942-49F1-B437-735099289BC8" }  
            }

            //, {
            //  path: 'deals'
            //  , loadChildren: () => import('./app/features/prob/prob-deals.routes')
            //    .then((r => r.PROB_DEALS_ROUTES))
            //}
            , { path: 'error/404', component: PageNotFoundComponent }
            , { path: 'error/not-authorized', component: PageNotAuthorizedComponent }
            , { path: 'not-authorized', redirectTo: 'error/not-authorized' }
            , { path: 'no-license', component: PageNoLicenseComponent }
            , { path: '**', redirectTo: 'error/404' }
          ])
        ]
      })
      .catch(err => console.log(err));

  })
